import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { ProductIssueType } from 'webapp/enums/enums';

import themeClasses from 'webapp/helpers/theme-classes';
import onHoverPopup from 'webapp/modifiers/on-hover-popup';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args as BaseArgs } from 'webapp/components/data-status-base';
import DataStatusBaseComponent from 'webapp/components/data-status-base';

import { StatusIconColor } from 'webapp/models-custom/status-object';

interface Args extends BaseArgs {
	theme: string;
	iconSize?: IconSize;
}

export default class DataStatusIconComponent<T extends Args> extends DataStatusBaseComponent<T> {
	@service('issues-report') declare issuesReportService: Service.IssuesReport;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	offset = 0;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const statusObjects = this.args.statusObjects;
		if (statusObjects) {
			statusObjects.forEach((statusObject) => {
				if (statusObject.shouldLogEventIssue) {
					this.issuesReportService.triggerEventIssue({
						type: ProductIssueType.DATA_WARNING,
						page: window.location.pathname,
						url: window.location.href,
						errorMessage: statusObject.title,
						endpoint: 'pusher',
					});
				}
			});
		}
	}

	get hasStatusObjects() {
		return (this.args.statusObjects || []).length > 0;
	}

	get isAnyStatusObjectRed() {
		return (this.args.statusObjects || []).some((so) => {
			return so.statusIconColor === StatusIconColor.RED;
		});
	}

	get iconClass(): IconName {
		return this.hasStatusObjects ? 'AlertFill' : 'InfoFill';
	}

	get colorClass(): string {
		return this.isAnyStatusObjectRed
			? StatusIconColor.RED
			: this.hasStatusObjects
				? StatusIconColor.ORANGE
				: StatusIconColor.DEFAULT;
	}

	get iconSize(): IconSize {
		return this.args.iconSize ?? 'sm';
	}
	<template>
		<div class="data-status-icon data-status-icon-for-{{@context}} {{themeClasses @theme}}">
			{{#if this.navigationService.isMobileApp}}
				<span
					role="button"
					class="color--{{this.colorClass}}"
					{{on "click" this.clickAction}}
				>
					<CommonUiIconComponent @name={{this.iconClass}} @size={{this.iconSize}} />
				</span>
			{{else}}
				<span class="color--{{this.colorClass}}" {{onHoverPopup this.openPopup}}>
					<CommonUiIconComponent @name={{this.iconClass}} @size={{this.iconSize}} />
				</span>
			{{/if}}
		</div>
	</template>
}
